/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2019 (original work) Open Assessment Technologies SA ;
 */

/**
 * Generate element id for node
 * @param {String} nodeType
 * @param {number} length
 * @param {String} platformPrefix
 * @returns {String}
 */
export function generateElementId(nodeType, length = 8, platformPrefix = 'tao') {
    const allLowerAlpha = [...'abcdefghijklmnopqrstuvwxyz'];
    const allNumbers = [...'0123456789'];
    const base = [...allNumbers, ...allLowerAlpha];

    const hash = [...Array(length)].map(() => base[Math.floor(Math.random() * base.length)]).join('');
    return `${platformPrefix}-${nodeType}-${hash}`;
}

/**
 * Gets keyboard-focusable elements within a specified element
 * Adapted from https://github.com/zellwk/zellwk.com/blob/v2.0.0/src/posts/2020-01-29-getting-focusable-elements.md
 * @param {HTMLElement} [element=document] element
 * @returns {Array}
 */
export function getKeyboardFocusableElements(element = document) {
    return [
        ...element.querySelectorAll('a, button, input:not([type="hidden"]), textarea, select, summary, [tabindex]')
    ].filter(
        el =>
            el.getAttribute('tabindex') !== '-1' &&
            !el.hasAttribute('disabled') &&
            el.offsetParent !== null /* is visible */
    );
}

/**
 * Tells if an element is rendered as right-to-left
 * @param {DOMElement} element
 * @returns {Boolean}
 */
export function isRTLElement(element) {
    return window.getComputedStyle(element).getPropertyValue('direction') === 'rtl';
}

/**
 * Get pointer coordinates from mousedown/mousemove/mouseup/touchstart/touchmove/touchend event
 * @param {MouseEvent|TouchEvent} e
 * @returns {Object} coordinates object: {x, y}
 */
export function getPointerEventCoords(e) {
    if (e.changedTouches) {
        e = e.changedTouches[0];
    }
    return { x: e.clientX, y: e.clientY };
}
